import React, { useState, useRef, useCallback, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { NodeViewProps } from '@tiptap/core';
import { Crop, X, Edit3, Loader } from 'lucide-react';
import { ImageEditor } from './ImageEditor';
import { ResizeHandles } from './ResizeHandles';
import ImageAnnotationEditor from './annotation';
import useSubscriptionStore from '../../store/useSubscriptionStore';
import { api } from '../../api/client';
import './CustomImageNodeView.css';

const CustomImageNodeView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  deleteNode,
  getPos,
  editor,
}) => {
  const [isSelected, setIsSelected] = useState(false);
  const [showCropEditor, setShowCropEditor] = useState(false);
  const [showAnnotationEditor, setShowAnnotationEditor] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const imageRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cachedImageRef = useRef<HTMLImageElement | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const croppedImageDataRef = useRef<string | null>(null);
  const [dynamicInitials, setDynamicInitials] = useState('?');
  const { hasPremiumFeatures } = useSubscriptionStore();

  const { src, alt, title, width, height, isAvatar } = node.attrs;
  const imageId: string | undefined = node.attrs?.['data-image-id'];
  const refreshTriedRef = useRef<boolean>(false);

  const displaySrc = node.attrs['data-annotated-data'] || node.attrs['src'] || src;
  

  const getDynamicInitials = useCallback(() => {
    if (!isAvatar || !editor || !getPos) {
      return node.attrs['data-fallback-initials'] || '??';
    }
    
    try {
      const pos = getPos();
      if (pos === undefined) {
        return node.attrs['data-fallback-initials'] || '??';
      }
      const $pos = editor.state.doc.resolve(pos);
      const paragraph = $pos.parent;
      
      let nameText = '';
      let foundAvatar = false;
      
      paragraph.content.forEach((node) => {
        if (node.type.name === 'customImage' && node.attrs && node.attrs.isAvatar) {
          foundAvatar = true;
        } else if (foundAvatar && node.type.name === 'text' && node.text && node.text.trim()) {
          nameText = node.text;
          foundAvatar = false;
        }
      });
      
      if (nameText) {
        const words = nameText.trim().split(/\s+/).filter(word => word.length > 0);
        if (words.length > 0) {
          const initials = words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
          return initials || '?';
        }
      }
    } catch (error) {
      console.log('Error calculating dynamic initials:', error);
    }
    
    return node.attrs['data-fallback-initials'] || '?';
  }, [isAvatar, editor, getPos, node.attrs]);

  useEffect(() => {
    if (isAvatar && editor) {
      const updateInitials = () => {
        const newInitials = getDynamicInitials();
        setDynamicInitials(newInitials);
      };
      
      updateInitials();
      
      editor.on('update', updateInitials);
      
      return () => {
        editor.off('update', updateInitials);
      };
      } else {
        setDynamicInitials(node.attrs['data-fallback-initials'] || '?');
      }
  }, [isAvatar, editor, getDynamicInitials, node.attrs]);

  const drawCanvas = useCallback(() => {
    if (!imageRef.current || !cachedImageRef.current) return;
    
    const canvas = imageRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = width;
    canvas.height = height;
    
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(cachedImageRef.current, 0, 0, width, height);
  }, [width, height]);

  useEffect(() => {
    // Empty src or `image://uuid` placeholder means we never got a resolved URL.
    // Skip the `new Image()` round-trip (which would silently fail on `image://`
    // or load the current page on `""`, masking the real problem) and go straight
    // to /download_urls so the image still renders.
    const needsResolve =
      !displaySrc ||
      (typeof displaySrc === 'string' && displaySrc.startsWith('image://'));

    if (needsResolve) {
      if (!imageId || typeof imageId !== 'string') {
        setImageError(true);
        setIsLoading(false);
        cachedImageRef.current = null;
        return;
      }
      setImageError(false);
      setIsLoading(true);
      refreshTriedRef.current = true;
      let cancelled = false;
      (async () => {
        try {
          const normalizedId = imageId.replace(/^image:\/\//, '');
          const res = await api.post('/download_urls', { image_ids: [normalizedId] });
          const nextUrl: string | undefined = res?.data?.urls?.[normalizedId];
          if (cancelled) return;
          if (nextUrl) {
            updateAttributes({ src: nextUrl });
            return;
          }
          setImageError(true);
          setIsLoading(false);
          cachedImageRef.current = null;
        } catch {
          if (cancelled) return;
          setImageError(true);
          setIsLoading(false);
          cachedImageRef.current = null;
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    setImageError(false);
    setIsLoading(true);
    refreshTriedRef.current = false;
    const img = new Image();
    img.onload = () => {
      cachedImageRef.current = img;
      setImageError(false);
      setIsLoading(false);
      setTimeout(() => {
        drawCanvas();
      }, 10);
    };
    img.onerror = async () => {
      // If the current src failed (likely stale presigned URL), try to fetch a fresh URL
      if (!refreshTriedRef.current && imageId && typeof imageId === 'string') {
        refreshTriedRef.current = true;
        setIsLoading(true);
        try {
          const normalizedId = imageId.replace(/^image:\/\//, '');
          const res = await api.post('/download_urls', { image_ids: [normalizedId] });
          const nextUrl: string | undefined = res?.data?.urls?.[normalizedId];
          if (nextUrl) {
            updateAttributes({ src: nextUrl });
            return;
          }
        } catch {
          // ignore; fall through to error state
        }
      }
      setImageError(true);
      setIsLoading(false);
      cachedImageRef.current = null;
    };
    img.src = displaySrc;
  }, [displaySrc, drawCanvas, imageId, updateAttributes]);

  useEffect(() => {
    if (!isResizing) {
      drawCanvas();
    }
  }, [width, height, isResizing, drawCanvas]);

  useEffect(() => {
    if (!isLoading && !imageError && cachedImageRef.current) {
      drawCanvas();
    }
  }, [isLoading, imageError, drawCanvas]);

  const handleMouseDown = useCallback(() => {
    setIsSelected(true);
  }, []);

  const handleResizeStart = useCallback((e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = width;
    const startHeight = height;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      let newWidth = startWidth;
      let newHeight = startHeight;

      const minSize = isAvatar ? 16 : 50;
      
      switch (handle) {
        case 'se': // Southeast (bottom-right)
          newWidth = Math.max(minSize, startWidth + deltaX);
          newHeight = Math.max(minSize, startHeight + deltaY);
          break;
        case 'sw': // Southwest (bottom-left)
          newWidth = Math.max(minSize, startWidth - deltaX);
          newHeight = Math.max(minSize, startHeight + deltaY);
          break;
        case 'ne': // Northeast (top-right)
          newWidth = Math.max(minSize, startWidth + deltaX);
          newHeight = Math.max(minSize, startHeight - deltaY);
          break;
        case 'nw': // Northwest (top-left)
          newWidth = Math.max(minSize, startWidth - deltaX);
          newHeight = Math.max(minSize, startHeight - deltaY);
          break;
        case 'e': // East (right)
          newWidth = Math.max(minSize, startWidth + deltaX);
          break;
        case 'w': // West (left)
          newWidth = Math.max(minSize, startWidth - deltaX);
          break;
        case 's': // South (bottom)
          newHeight = Math.max(minSize, startHeight + deltaY);
          break;
        case 'n': // North (top)
          newHeight = Math.max(minSize, startHeight - deltaY);
          break;
      }

      updateAttributes({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width, height, updateAttributes, isAvatar]);

  const handleCrop = useCallback(() => {
    setShowCropEditor(true);
  }, []);

  const handleCropSave = useCallback((croppedImageUrl: string, newWidth: number, newHeight: number) => {
    croppedImageDataRef.current = croppedImageUrl;
    
    updateAttributes({ 
      src: croppedImageUrl,
      width: newWidth,
      height: newHeight,
      'data-is-cropped': 'true',
      'data-cropped-data': croppedImageUrl
    });
    setShowCropEditor(false);
  }, [updateAttributes]);

  const handleCropCancel = useCallback(() => {
    setShowCropEditor(false);
  }, []);

  const handleAnnotation = useCallback(() => {
    if (!hasPremiumFeatures) {

      alert('Image annotation is a premium feature. Please upgrade to access this functionality.');
      return;
    }
    setShowAnnotationEditor(true);
  }, [hasPremiumFeatures]);

  const handleAnnotationSave = useCallback((annotatedImageUrl: string) => {

    const imageId = node.attrs['data-image-id'] || `image-${Date.now()}`;
    
    updateAttributes({ 
      src: annotatedImageUrl,
      'data-is-annotated': 'true',
      'data-annotated-data': annotatedImageUrl,
      'data-image-id': imageId
    });
    setShowAnnotationEditor(false);
    
    if (editor) {

      const currentContent = editor.getHTML();
      editor.commands.setContent(currentContent + ' ', { emitUpdate: false });
      setTimeout(() => {
        editor.commands.setContent(currentContent, { emitUpdate: false });
      }, 1);
    }
  }, [updateAttributes, node.attrs, editor]);

  const handleAnnotationCancel = useCallback(() => {
    setShowAnnotationEditor(false);
  }, []);

  const handleDelete = useCallback(() => {
    deleteNode();
  }, [deleteNode]);

  useEffect(() => {
    if (!isSelected) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsSelected(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelected]);

  return (
    <>
      <NodeViewWrapper style={{ display:  'inline-block' }}>
        <div
          ref={containerRef}
          className="custom-image-container"
          draggable
          data-drag-handle
          data-avatar={isAvatar ? "true" : undefined}
          style={{
            position: 'relative',
            display: 'inline-block',
            maxWidth: 'none',
            maxHeight: 'none',
            minWidth: '0',
            minHeight: '0',
            cursor: isSelected ? 'grab' : 'pointer',
            verticalAlign: 'middle',
          }}
          onMouseDown={handleMouseDown}
          onDragStart={() => {
            if (containerRef.current) containerRef.current.style.opacity = '0.5'
          }}
          onDragEnd={() => {
            if (containerRef.current) containerRef.current.style.opacity = '1'
          }}
        >
          {isLoading ? (
            <div
              style={{
                width: `${width}px`,
                height: `${height}px`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                borderRadius: isAvatar ? '50%' : '4px',
                transition: isResizing ? 'none' : 'all 0.2s ease',
                margin: 0,
                padding: 0,
                cursor: 'inherit',
                verticalAlign: 'middle',
              }}
              title="Loading..."
              role="img"
              aria-label="Loading image"
            >
              <Loader className="animate-spin" style={{ width: `${Math.min(width, height) * 0.3}px`, height: `${Math.min(width, height) * 0.3}px` }} />
            </div>
          ) : !imageError ? (
            <canvas
              ref={imageRef}
              style={{
                width: `${width}px`,
                height: `${height}px`,
                display: 'inline-block',
                border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                borderRadius: isAvatar ? '50%' : '4px',
                transition: isResizing ? 'none' : 'all 0.2s ease',
                margin: 0,
                padding: 0,
                maxWidth: 'none',
                maxHeight: 'none',
                minWidth: '0',
                minHeight: '0',
                cursor: 'inherit',
                verticalAlign: 'middle',
              }}
              title={title}
              role="img"
              aria-label={alt}
            />
          ) : (
            <div
              style={{
                width: `${width}px`,
                height: `${height}px`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#6b7280',
                color: 'white',
                fontSize: `${Math.min(width, height) * 0.3}px`,
                fontWeight: 'bold',
                border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                borderRadius: isAvatar ? '50%' : '4px',
                transition: isResizing ? 'none' : 'all 0.2s ease',
                margin: 0,
                padding: 0,
                cursor: 'inherit',
                verticalAlign: 'middle',
              }}
              title={title}
              role="img"
              aria-label={alt}
            >
              {dynamicInitials}
            </div>
          )}
            {isSelected && <>
              <ResizeHandles
                isSelected={isSelected}
                onResizeStart={handleResizeStart}
                handleOffset={-6} // Outside the container
              />
              {/* Action buttons */}
              <div
                style={{
                  position: 'absolute',
                  top: '-30px',
                  right: '0',
                  display: 'flex',
                  gap: '4px',
                  zIndex: 10,
                }}
              >
                {!isAvatar && !imageError && !isLoading && (
                  <>
                  <button
                    onClick={handleCrop}
                    style={{
                      padding: '6px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Crop Image"
                  >
                    <Crop size={14} />
                  </button>
                  <button
                  onClick={handleAnnotation}
                  style={{
                    padding: '6px',
                    background: hasPremiumFeatures ? '#10b981' : '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: hasPremiumFeatures ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: hasPremiumFeatures ? 1 : 0.6,
                  }}
                  title={hasPremiumFeatures ? "Annotate Image" : "Annotate Image (Premium)"}
                  disabled={!hasPremiumFeatures}
                >
                  <Edit3 size={14} />
                </button>
                </>
                )}
                <button
                  onClick={handleDelete}
                  style={{
                    padding: '6px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Delete Image"
                >
                  <X size={14} />
                </button>
              </div>
            </>}
        </div>
      </NodeViewWrapper>

      {/* Image Editor Modal */}
      {showCropEditor && (
        <ImageEditor
          src={displaySrc}
          onSave={handleCropSave}
          onCancel={handleCropCancel}
          initialWidth={width}
          initialHeight={height}
        />
      )}

      {/* Image Annotation Modal */}
      {showAnnotationEditor && (
        <ImageAnnotationEditor
          src={displaySrc}
          onSave={handleAnnotationSave}
          onCancel={handleAnnotationCancel}
          initialWidth={width}
          initialHeight={height}
          imageId={imageId}
        />
      )}
    </>
  );
};

export default CustomImageNodeView;